import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Bars3BottomLeftIcon, HashtagIcon, ListBulletIcon, TableCellsIcon,
  PhotoIcon, VideoCameraIcon, ArrowUpIcon, ArrowDownIcon, TrashIcon, PlusIcon,
  EyeIcon, XMarkIcon, LinkIcon, HandRaisedIcon, DocumentTextIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import MessageBlocks from './MessageBlocks';
import DocumentViewer from './DocumentViewer';

let seq = 0;
const newId = () => `b${Date.now()}_${seq++}`;

export function blankBlock(type) {
  switch (type) {
    case 'heading': return { id: newId(), type, text: '' };
    case 'bullets': return { id: newId(), type, items: [''] };
    case 'table': return { id: newId(), type, rows: [['', ''], ['', '']] };
    // text is the wording shown; url is where it goes. Keeping them separate is
    // the whole point — the reader sees "Book your slot", not a 90-character
    // address with a tracking query on the end.
    case 'link': return { id: newId(), type, url: '', text: '' };
    case 'image': return { id: newId(), type, dataUri: '', alt: '' };
    case 'video': return { id: newId(), type, kind: 'link', url: '', mediaId: null, token: null, mime: null, name: '' };
    // `source` (library | upload | link), deliberately not `kind`: the server's
    // video tracking treats any block with kind 'upload' as a video to watch.
    case 'document': return { id: newId(), type, source: 'library', title: '', url: '', name: '', mediaId: null, token: null, mime: null, size: null };
    default: return { id: newId(), type: 'text', text: '' };
  }
}

const isHttps = (u) => typeof u === 'string' && /^https:\/\/\S+$/i.test(u.trim());

const URL_IN_TEXT = /https?:\/\/[^\s<>]+/i;

// The words a block holds, one entry per line/point/row -- what survives when
// the block is turned into another type.
function blockLines(b) {
  switch (b.type) {
    case 'heading': return [b.text || ''];
    case 'text': return (b.text || '').split('\n');
    case 'bullets': return b.items || [];
    case 'table': return (b.rows || []).map((r) => r.filter((c) => c.trim()).join(' – '));
    case 'link': return [b.text || b.url || ''];
    case 'video': return b.kind === 'link' ? [b.url || ''] : [];
    case 'document': return [b.title || (b.source === 'upload' ? '' : b.url) || ''];
    default: return [];
  }
}

function blockUrl(b) {
  if (b.type === 'link' || (b.type === 'video' && b.kind === 'link')) return (b.url || '').trim();
  if (b.type === 'document') return b.source === 'upload' ? '' : (b.url || '').trim();
  const m = blockLines(b).join('\n').match(URL_IN_TEXT);
  return m ? m[0] : '';
}

// Would switching this block to `type` throw away something the author made?
// Images and uploaded videos can't become anything else; wording can't become
// an image. Tables flatten to one line per row, which keeps the words.
export function conversionLosesContent(b, type) {
  if (b.type === type || !blockHasContent(b)) return false;
  if (b.type === 'image' || (b.type === 'video' && b.kind === 'upload') || (b.type === 'document' && b.source === 'upload')) return true;
  if (type === 'image') return true;
  // A worded link becoming plain text keeps the words but drops the address.
  if (b.type === 'link' && b.text?.trim() && b.url?.trim() && type !== 'video') return true;
  // A video keeps only an address; any other wording goes.
  if (type === 'video') { const url = blockUrl(b); return !url || blockLines(b).some((l) => l.trim() && l.trim() !== url); }
  return false;
}

// Turn a block into another type, carrying over what fits. Keeps the id, so the
// block stays where it is and a drag in progress isn't disturbed.
export function convertBlock(b, type) {
  if (b.type === type) return b;
  const lines = blockLines(b).map((l) => l.trim()).filter(Boolean);
  const url = blockUrl(b);
  const next = { ...blankBlock(type), id: b.id };
  switch (type) {
    case 'heading': next.text = lines.join(' '); break;
    case 'text': next.text = lines.join('\n'); break;
    case 'bullets': next.items = lines.length ? lines : ['']; break;
    case 'table': if (lines.length) next.rows = lines.map((l) => [l, '']); break;
    case 'link': next.url = url; next.text = b.type === 'link' ? b.text : lines.filter((l) => l !== url).join(' '); break;
    case 'video': next.url = url; break;
    // Becomes a pasted link; the author can switch it to a library pick.
    case 'document': next.source = 'link'; next.url = url; next.title = lines.filter((l) => l !== url).join(' '); break;
    default: break;
  }
  return next;
}

// Blocks are "empty" if they carry nothing renderable — used to validate + trim.
export function blockHasContent(b) {
  if (b.type === 'heading' || b.type === 'text') return !!(b.text && b.text.trim());
  if (b.type === 'bullets') return b.items.some((i) => i.trim());
  if (b.type === 'table') return b.rows.some((r) => r.some((c) => c.trim()));
  // The address is what makes a link block worth sending; wording is optional
  // and falls back to the address when it is left blank.
  if (b.type === 'link') return !!(b.url && b.url.trim());
  if (b.type === 'image') return !!b.dataUri;
  if (b.type === 'video') return b.kind === 'link' ? !!b.url.trim() : !!b.mediaId;
  if (b.type === 'document') return b.source === 'upload' ? !!b.mediaId : isHttps(b.url);
  return false;
}

// Uploaded files referenced by the message, tied to it on send.
export function uploadedMediaIds(blocks) {
  return blocks
    .filter((b) => b.mediaId && ((b.type === 'video' && b.kind === 'upload') || (b.type === 'document' && b.source === 'upload')))
    .map((b) => b.mediaId);
}

async function compressImage(file, maxDim = 1200, quality = 0.7) {
  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) { const s = Math.min(maxDim / width, maxDim / height); width = Math.round(width * s); height = Math.round(height * s); }
  const c = document.createElement('canvas'); c.width = width; c.height = height;
  c.getContext('2d').drawImage(img, 0, 0, width, height);
  return c.toDataURL('image/jpeg', quality);
}

const ADD_MENU = [
  { type: 'heading', label: 'Heading', Icon: HashtagIcon },
  { type: 'text', label: 'Text', Icon: Bars3BottomLeftIcon },
  { type: 'bullets', label: 'Bullets', Icon: ListBulletIcon },
  { type: 'table', label: 'Table', Icon: TableCellsIcon },
  { type: 'link', label: 'Link', Icon: LinkIcon },
  { type: 'image', label: 'Image', Icon: PhotoIcon },
  { type: 'video', label: 'Video', Icon: VideoCameraIcon },
  { type: 'document', label: 'Document', Icon: DocumentTextIcon },
];

const inputCls = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

// A text box that grows with what is typed into it (from three lines up), so a
// long paragraph is read and edited whole rather than through a small scrolling
// slot. Re-measured when the text changes and when the box's width changes --
// the builder column narrows and widens with the window.
function AutoGrowTextarea({ value, className, ...props }) {
  const ref = useRef(null);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight leaves out the border; the box is border-box.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + border}px`;
  };
  useLayoutEffect(fit, [value]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== width) { width = el.clientWidth; fit(); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <textarea ref={ref} rows={3} value={value} className={`${className} resize-none overflow-hidden`} {...props} />;
}

const DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';
const stripExt = (n) => String(n || '').replace(/\.[a-z0-9]{1,5}$/i, '');
const fmtBytes = (n) => (!n ? '' : n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Document block: pick from the Nano Document Library, upload a file made
// outside the IMS, or paste a link to a document that lives somewhere else.
function DocumentEditor({ block, update }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const source = block.source || 'library';
  const chosen = source === 'upload' ? !!block.mediaId : isHttps(block.url);

  useEffect(() => {
    if (source !== 'library') return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    let live = true;
    // Typing pauses before searching, so SharePoint isn't asked on every key.
    const t = setTimeout(async () => {
      setSearching(true); setErr('');
      try {
        const { data } = await api.get('/api/comms/library', { params: { q: term } });
        if (live) setResults(data.documents || []);
      } catch (e) {
        if (live) { setResults([]); setErr(e.response?.data?.error || 'The library could not be searched.'); }
      } finally { if (live) setSearching(false); }
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [q, source]);

  // Switching source starts that source clean; the title the author typed stays.
  const setSource = (s) => update({ source: s, itemId: null, url: '', name: '', mediaId: null, token: null, mime: null, size: null });
  const tab = (s, label) => (
    <button type="button" onClick={() => s !== source && setSource(s)}
      className={`rounded-md px-2 py-1 text-xs font-medium ${source === s ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{label}</button>
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {tab('library', 'Nano Document Library')}
        {tab('upload', 'Upload a file')}
        {tab('link', 'Paste a link')}
      </div>

      {chosen && source !== 'link' ? (
        <div className="flex items-center gap-2 rounded-md border border-brand/30 bg-brand-light/30 px-2 py-1.5 text-sm">
          <DocumentTextIcon className="h-5 w-5 shrink-0 text-brand" />
          <span className="min-w-0 flex-1 truncate">{block.name}{block.size ? <span className="text-xs text-gray-400"> · {fmtBytes(block.size)}</span> : null}</span>
          <button type="button" onClick={() => setSource(source)} className="shrink-0 text-xs font-medium text-brand hover:text-brand-dark">Change</button>
        </div>
      ) : source === 'library' ? (
        <div>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the library, e.g. road crossing guide" className={`${inputCls} pl-7`} />
          </div>
          {searching && <p className="mt-1 text-xs text-gray-400">Searching…</p>}
          {results && !searching && (
            <ul className="mt-1 max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {results.map((d) => (
                <li key={d.id}>
                  <button type="button"
                    onClick={() => update({ source: 'library', itemId: d.id /* lets the app show it, fresh from the library */, url: d.url, name: d.name, mime: d.mime, size: d.size, title: block.title || stripExt(d.name) })}
                    className="block w-full px-2 py-1.5 text-left hover:bg-brand-light/40">
                    <span className="block truncate text-sm text-gray-800">{d.name}</span>
                    <span className="block truncate text-[11px] text-gray-400">{[d.folder, fmtBytes(d.size)].filter(Boolean).join(' · ')}</span>
                  </button>
                </li>
              ))}
              {results.length === 0 && !err && <li className="px-2 py-2 text-xs text-gray-400">No documents match.</li>}
            </ul>
          )}
        </div>
      ) : source === 'upload' ? (
        <div>
          <input type="file" accept={DOC_ACCEPT} onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            setBusy(true); setErr('');
            try {
              // kind goes before the file so the server has it when the file arrives.
              const fd = new FormData(); fd.append('kind', 'document'); fd.append('file', f);
              const { data } = await api.post('/api/comms/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              update({ source: 'upload', mediaId: data.id, token: data.token, mime: data.mime, size: data.size, name: f.name, title: block.title || stripExt(f.name) });
            } catch (ex) { setErr(ex.response?.data?.error || 'Upload failed.'); } finally { setBusy(false); }
          }} className="text-sm" />
          <p className="mt-1 text-[11px] text-gray-400">PDF, Word, Excel, PowerPoint, TXT or CSV, up to 50 MB. Uploaded PDFs open inside the app; other uploads download — put Office files in the library to have them shown in the app.</p>
          {busy && <p className="mt-1 text-xs text-gray-400">Uploading…</p>}
        </div>
      ) : (
        <div>
          <input value={block.url || ''} onChange={(e) => update({ url: e.target.value.trim() })} placeholder="https://…" className={`${inputCls} font-mono text-xs`} />
          {!!block.url && !isHttps(block.url) && <p className="mt-1 text-[11px] font-medium text-amber-600">Needs to start with https:// — without it the document will not appear.</p>}
        </div>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}

      <input value={block.title || ''} onChange={(e) => update({ title: e.target.value })}
        placeholder="Title shown to readers, e.g. Road Crossing quick guide" className={inputCls} />
    </div>
  );
}

function BlockEditor({ block, update }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (block.type === 'document') return <DocumentEditor block={block} update={update} />;
  if (block.type === 'heading') {
    return <input value={block.text} onChange={(e) => update({ text: e.target.value })} placeholder="Heading" className={`${inputCls} text-base font-semibold`} />;
  }
  if (block.type === 'text') {
    return <AutoGrowTextarea value={block.text} onChange={(e) => update({ text: e.target.value })} placeholder="Write text…" className={inputCls} />;
  }
  if (block.type === 'bullets') {
    return (
      <div className="space-y-1">
        {block.items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-gray-400">•</span>
            <input value={it} onChange={(e) => { const items = [...block.items]; items[i] = e.target.value; update({ items }); }} placeholder={`Point ${i + 1}`} className={inputCls} />
            <button type="button" onClick={() => update({ items: block.items.filter((_, x) => x !== i) })} className="text-gray-300 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => update({ items: [...block.items, ''] })} className="text-xs font-medium text-brand hover:text-brand-dark">+ Add point</button>
      </div>
    );
  }
  if (block.type === 'link') {
    const u = (block.url || '').trim();
    // Anything that is not http(s) or an internal path is dropped by the
    // renderer, so it is flagged here rather than silently vanishing from the
    // message after it has been sent.
    const badUrl = !!u && !/^\//.test(u) && !/^https?:\/\//i.test(u);
    return (
      <div className="space-y-1.5">
        <input
          value={block.text || ''}
          onChange={(e) => update({ text: e.target.value })}
          placeholder="Link text — what the reader sees, e.g. Book your slot"
          className={inputCls}
        />
        <input
          value={block.url || ''}
          onChange={(e) => update({ url: e.target.value })}
          placeholder="https://…"
          className={`${inputCls} font-mono text-xs`}
        />
        {badUrl && (
          <p className="text-[11px] font-medium text-amber-600">
            Needs to start with https:// — without it the link will not appear in the message.
          </p>
        )}
        {!block.text?.trim() && !!u && !badUrl && (
          <p className="text-[11px] text-gray-400">No link text — the full address will be shown.</p>
        )}
      </div>
    );
  }
  if (block.type === 'table') {
    const setCell = (r, c, v) => { const rows = block.rows.map((row) => [...row]); rows[r][c] = v; update({ rows }); };
    const addRow = () => update({ rows: [...block.rows, block.rows[0].map(() => '')] });
    const addCol = () => update({ rows: block.rows.map((row) => [...row, '']) });
    return (
      <div>
        <table className="w-full border-collapse text-sm">
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} className="border border-gray-200 p-0">
                    <input value={cell} onChange={(e) => setCell(r, c, e.target.value)} placeholder={r === 0 ? 'Header' : ''} className={`w-full px-2 py-1 text-sm focus:outline-none ${r === 0 ? 'font-semibold' : ''}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-1 flex gap-3 text-xs">
          <button type="button" onClick={addRow} className="font-medium text-brand hover:text-brand-dark">+ Row</button>
          <button type="button" onClick={addCol} className="font-medium text-brand hover:text-brand-dark">+ Column</button>
          <span className="ml-auto text-gray-400">First row = header</span>
        </div>
      </div>
    );
  }
  if (block.type === 'image') {
    return (
      <div>
        {block.dataUri
          ? <img src={block.dataUri} alt={block.alt || ''} className="mb-2 max-h-52 rounded-md border border-gray-200" />
          : <p className="mb-2 text-sm text-gray-400">No image yet.</p>}
        <input type="file" accept="image/*" onChange={async (e) => {
          const f = e.target.files?.[0]; if (!f) return;
          setBusy(true); setErr('');
          try { update({ dataUri: await compressImage(f) }); } catch { setErr('Could not read that image.'); } finally { setBusy(false); }
        }} className="text-sm" />
        {busy && <p className="mt-1 text-xs text-gray-400">Processing…</p>}
        {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
      </div>
    );
  }
  if (block.type === 'video') {
    return (
      <div className="space-y-2">
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-1.5"><input type="radio" checked={block.kind === 'link'} onChange={() => update({ kind: 'link' })} /> Link</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={block.kind === 'upload'} onChange={() => update({ kind: 'upload' })} /> Upload</label>
        </div>
        {block.kind === 'link' ? (
          <input value={block.url} onChange={(e) => update({ url: e.target.value })} placeholder="Paste a SharePoint / Stream / YouTube / Vimeo link" className={inputCls} />
        ) : (
          <div>
            {block.mediaId
              ? <p className="mb-1 text-sm text-emerald-600">Uploaded: {block.name}</p>
              : <p className="mb-1 text-sm text-gray-400">No video uploaded yet.</p>}
            <input type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              setBusy(true); setErr('');
              try {
                const fd = new FormData(); fd.append('file', f);
                const { data } = await api.post('/api/comms/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                update({ mediaId: data.id, token: data.token, mime: data.mime, name: f.name });
              } catch (ex) { setErr(ex.response?.data?.error || 'Upload failed.'); } finally { setBusy(false); }
            }} className="text-sm" />
            {busy && <p className="mt-1 text-xs text-gray-400">Uploading…</p>}
            {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
          </div>
        )}
      </div>
    );
  }
  return null;
}

// `livePreview`: the screen shows a live preview beside the builder on wide
// screens, so the Preview button is only needed below that width.
function MessageBuilder({ blocks, onChange, livePreview = false }) {
  const [preview, setPreview] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const update = (id, patch) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const add = (type) => onChange([...blocks, blankBlock(type)]);
  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));
  const move = (id, dir) => {
    const i = blocks.findIndex((b) => b.id === id); const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const cp = [...blocks]; [cp[i], cp[j]] = [cp[j], cp[i]]; onChange(cp);
  };
  const changeType = (b, type) => {
    if (conversionLosesContent(b, type)
      && !window.confirm(`Changing this ${b.type} block to ${type} will lose its content. Change it anyway?`)) return;
    onChange(blocks.map((x) => (x.id === b.id ? convertBlock(x, type) : x)));
  };

  // Drag to reorder. Only the grip is draggable -- a draggable card would turn
  // selecting text in its inputs into a drag -- but the whole card is used as
  // the drag image. `dropAt` is the gap the block will land in (0 = above the
  // first block, blocks.length = below the last). The arrows stay for keyboard users.
  const [dragId, setDragId] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  // The block whose hand is held down -- outlined from the moment it is
  // grabbed, so the author can see which block they have hold of before moving.
  const [grabId, setGrabId] = useState(null);
  useEffect(() => {
    if (!grabId) return undefined;
    const release = () => setGrabId(null); // a grab that never became a drag
    window.addEventListener('mouseup', release);
    return () => window.removeEventListener('mouseup', release);
  }, [grabId]);
  const endDrag = () => { setDragId(null); setDropAt(null); setGrabId(null); };
  const from = dragId ? blocks.findIndex((b) => b.id === dragId) : -1;
  // Dropping a block into the gap directly above or below itself changes nothing,
  // so no line is drawn there.
  const showLineAt = (gap) => from !== -1 && dropAt === gap && gap !== from && gap !== from + 1;
  const drop = () => {
    if (showLineAt(dropAt)) {
      const cp = [...blocks];
      const [moved] = cp.splice(from, 1);
      cp.splice(dropAt > from ? dropAt - 1 : dropAt, 0, moved);
      onChange(cp);
    }
    endDrag();
  };
  // Overlaid in the gap rather than inserted into the flow: a line that pushed
  // the blocks apart would move the midpoint under the cursor and flicker.
  const dropLine = (pos) => <div className={`pointer-events-none absolute inset-x-0 h-1 rounded-full bg-brand ${pos === 'above' ? '-top-[6px]' : '-bottom-[6px]'}`} />;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="block text-xs font-medium text-gray-500">Message</label>
        {/* Renders through MessageBlocks -- the same component the engineer's
            app uses -- so the preview cannot drift from what is delivered. */}
        <button
          type="button"
          onClick={() => setPreview(true)}
          disabled={!blocks.some(blockHasContent)}
          className={`flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:border-brand hover:text-brand disabled:opacity-40 ${livePreview ? 'lg:hidden' : ''}`}
        >
          <EyeIcon className="h-3.5 w-3.5" /> Preview
        </button>
      </div>

      {viewingDoc && <DocumentViewer block={viewingDoc} onClose={() => setViewingDoc(null)} />}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
          onClick={() => setPreview(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-xl rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Preview</h3>
                <p className="text-[11px] text-gray-400">How this looks to the person receiving it</p>
              </div>
              <button type="button" onClick={() => setPreview(false)} className="text-gray-400 hover:text-gray-700">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {/* A link in the preview must never take this tab away from the
                composer -- an internal /path link used to navigate in place and
                the unsent message went with it. Every link opens a new tab. */}
            <div
              className="px-4 py-4"
              role="presentation"
              onClickCapture={(e) => {
                const a = e.target.closest && e.target.closest('a[href]');
                if (!a || a.target === '_blank') return;
                e.preventDefault();
                window.open(a.href, '_blank', 'noopener,noreferrer');
              }}
            >
              {blocks.some(blockHasContent)
                ? <MessageBlocks blocks={blocks.filter(blockHasContent)} onOpenDocument={setViewingDoc} />
                : <p className="text-sm text-gray-400">Nothing to preview yet.</p>}
            </div>
            <div className="border-t border-gray-200 px-4 py-3 text-right">
              <button type="button" onClick={() => setPreview(false)} className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Only a block drag is handled here; text dragged into a textarea still drops there. */}
      <div className="space-y-2"
        onDragOver={(e) => { if (dragId) e.preventDefault(); }}
        onDrop={(e) => { if (!dragId) return; e.preventDefault(); drop(); }}>
        {blocks.map((b, i) => (
          <div
            key={b.id}
            data-block-card
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              setDropAt(e.clientY < r.top + r.height / 2 ? i : i + 1);
            }}
            className={`relative rounded-md border p-2 transition-shadow ${
              dragId === b.id ? 'border-brand bg-brand-light/30 opacity-60 ring-2 ring-brand/40'
                : grabId === b.id ? 'border-brand ring-2 ring-brand/40'
                  : 'border-gray-200'}`}
          >
            {showLineAt(i) && dropLine('above')}
            {i === blocks.length - 1 && showLineAt(blocks.length) && dropLine('below')}
            <div className="mb-1 flex items-center gap-2">
              <span
                draggable
                onDragStart={(e) => {
                  const card = e.currentTarget.closest('[data-block-card]');
                  e.dataTransfer.effectAllowed = 'move';
                  // Firefox won't start a drag without data. Not text/plain, or a
                  // text box it is dropped on would paste the block id.
                  e.dataTransfer.setData('application/x-comms-block', b.id);
                  if (card) {
                    const r = card.getBoundingClientRect();
                    e.dataTransfer.setDragImage(card, e.clientX - r.left, e.clientY - r.top);
                  }
                  // Next tick, so the drag image (already outlined by the grab)
                  // is taken before the card fades.
                  setTimeout(() => setDragId(b.id), 0);
                }}
                onDragEnd={endDrag}
                onMouseDown={() => setGrabId(b.id)}
                title="Drag to reorder"
                className={`cursor-grab active:cursor-grabbing ${grabId === b.id || dragId === b.id ? 'text-brand' : 'text-gray-400 hover:text-brand'}`}
              >
                <HandRaisedIcon className="h-4 w-4" />
              </span>
              <select
                value={b.type}
                onChange={(e) => changeType(b, e.target.value)}
                title="Change block type"
                aria-label="Block type"
                className="cursor-pointer rounded border border-transparent bg-transparent py-0 pl-1 pr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:border-gray-300 hover:text-gray-700 focus:border-brand focus:outline-none"
              >
                {ADD_MENU.map(({ type, label }) => <option key={type} value={type}>{label}</option>)}
              </select>
              <div className="ml-auto flex items-center gap-1">
                <button type="button" onClick={() => move(b.id, -1)} disabled={i === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ArrowUpIcon className="h-4 w-4" /></button>
                <button type="button" onClick={() => move(b.id, 1)} disabled={i === blocks.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ArrowDownIcon className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(b.id)} className="text-gray-300 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
              </div>
            </div>
            <BlockEditor block={b} update={(patch) => update(b.id, patch)} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2">
        <span className="mr-1 flex items-center text-xs font-medium text-gray-400">Add block:</span>
        {ADD_MENU.map(({ type, label, Icon }) => (
          <button key={type} type="button" onClick={() => add(type)}
            className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:border-brand hover:text-brand">
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-gray-400"><PlusIcon className="inline h-3 w-3" /> Build the message from blocks — text, headings, bullets, tables, links, images, video and documents.</p>
    </div>
  );
}

export default MessageBuilder;
